import React from 'react';
import { useSWRConfig } from 'swr';
import cloneDeep from 'lodash.clonedeep';
import { Button } from '@progress/kendo-react-buttons';
import { Input, TextArea } from '@progress/kendo-react-inputs';
import { Error as ErrorLabel } from '@progress/kendo-react-labels';
import { Window, WindowActionsBar } from '@progress/kendo-react-dialogs';
import {
  Upload,
  ExternalDropZone,
  UploadOnAddEvent
} from '@progress/kendo-react-upload';

import { useToast } from '../contexts/toast-context';
import { getCurrentTimeStamp } from '../utils/date';
import { PostResourceAsync } from '../services/files';
import { AddZoneLibraryAsync } from '../services/zone';
import { ZoneResponse } from '../types/responses/zone-response';
import { generateZoneSnapshot } from '../utils/generate-zone-snapshot';
import { ExportedZoneData } from '../types/game-document/entities/exported-zone';

export const ImportZoneWindow: React.FC<{ onClose: Function }> = ({
  onClose
}) => {
  const uploadRef = React.useRef<Upload>(null);
  const { onToastOpen } = useToast();
  const { mutate } = useSWRConfig();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [payload, setPayload] = React.useState<
    | {
        content?: ExportedZoneData;
        name?: string;
        description?: string;
      }
    | undefined
  >();

  const onUploadClick = () => {
    if (uploadRef && uploadRef.current && uploadRef.current.actionElement)
      uploadRef.current.actionElement.click();
  };

  const onAddFile = async (event: UploadOnAddEvent) => {
    setPayload(undefined);
    let errorMessage = '';
    let resultFile: File | undefined;
    event.newState.forEach((file) => {
      if (file) {
        if (file.validationErrors) {
          if (file.validationErrors.includes('invalidFileExtension'))
            errorMessage = `Invalid file type for: ${file.name}`;

          return;
        }

        if (file.getRawFile) resultFile = file.getRawFile();
      }
    });

    if (errorMessage) {
      setErrorMessage(errorMessage);
      return;
    } else {
      setErrorMessage('');
    }

    if (!resultFile) return;

    try {
      const json: ExportedZoneData = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(JSON.parse(e.target?.result as string));
        reader.onerror = reject;
        reader.readAsText(resultFile!);
      });

      if (json.zones.length) {
        setPayload({
          content: json,
          description: json.zones[0].description,
          name: json.zones[0].name
        });
      } else {
        throw new Error();
      }
    } catch (error) {
      setErrorMessage(
        'There is an error when reading json file. Please try again or input different file.'
      );
    }
  };

  const onSubmitImport = async () => {
    if (!payload) return;
    try {
      setIsLoading(true);
      const content = cloneDeep(payload.content);
      if (!content) return;

      const name = payload.name ?? '';
      const description = payload.description ?? '';

      content.zones[0].name = name;
      content.zones[0].description = description;
      content.maps[0].name = name;
      content.maps[0].description = description;

      const zoneBoundary = content.zones[0].boundary;
      let previewUrl = '';
      if (content.maps[0].type === 'illustrated') {
        previewUrl =
          content.resources.find((res) => res.id === content.maps[0].imageResId)
            ?.value ?? '';
      } else {
        const blob = await generateZoneSnapshot(zoneBoundary);
        const zoneName = content.zones[0].name ?? 'zone';
        const formattedTimestamp = getCurrentTimeStamp();
        const fileName = `zone-${zoneName}-preview-url-${formattedTimestamp}.jpeg`;

        const extractedBlob: File = new File([blob], fileName, {
          type: blob.type
        });

        // upload the file to azure blob
        const respUpload = await PostResourceAsync(extractedBlob);
        previewUrl = respUpload.value;
      }

      const data: ZoneResponse = {
        name,
        description,
        previewImageUrl: previewUrl,
        content
      };
      await AddZoneLibraryAsync(data);

      onToastOpen({
        title: 'Success',
        body: 'Zone imported to my library.',
        style: 'success'
      });

      mutate('get-all-zones');
    } catch (error) {
      onToastOpen({
        title: 'Error',
        body: 'Failed to import zone to my library.',
        style: 'error'
      });
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  return (
    <>
      <Upload
        disabled={isLoading}
        className={'d-none'}
        ref={uploadRef}
        batch={false}
        multiple={false}
        withCredentials={false}
        restrictions={{
          allowedExtensions: ['.json']
        }}
        accept={'.json'}
        onAdd={onAddFile}
        showFileList
      />

      <Window
        title={'Import from JSON'}
        initialWidth={640}
        initialHeight={400}
        onClose={() => onClose()}
        className={'zone-library'}>
        <ExternalDropZone
          disabled={isLoading}
          uploadRef={uploadRef}
          className={'mb-2'}
          customNote={'Only .json files are allowed.'}
          customHint={
            <div className={'d-flex flex-column gap-2'}>
              <span
                className="material-symbols-outlined"
                style={{
                  color: '#a5b7bf',
                  fontSize: '48px'
                }}>
                upload
              </span>
              <span>Drag and drop a json file here</span>
              <span
                style={{
                  fontSize: '14px'
                }}>
                or{' '}
                <span
                  className={'text-primary cursor-pointer'}
                  onClick={onUploadClick}>
                  browse
                </span>
              </span>
            </div>
          }
        />
        {errorMessage && (
          <ErrorLabel className={'m-0'}>{errorMessage}</ErrorLabel>
        )}

        {payload && (
          <>
            <div className="mb-3">
              <label className="k-label">Name</label>
              <Input
                value={payload.name}
                onChange={(e) =>
                  setPayload((prev) => ({ ...prev, name: e.value }))
                }
                style={{ width: '100%' }}
                disabled={isLoading}
              />
            </div>
            <div className="mb-3">
              <label className="k-label">Description</label>
              <TextArea
                value={payload.description}
                onChange={(e) =>
                  setPayload((prev) => ({ ...prev, description: e.value }))
                }
                style={{ width: '100%' }}
                disabled={isLoading}
              />
            </div>
          </>
        )}

        <WindowActionsBar>
          <Button themeColor={'secondary'} onClick={() => onClose()}>
            Cancel
          </Button>
          <Button
            themeColor={'primary'}
            onClick={onSubmitImport}
            disabled={
              errorMessage !== '' ||
              !payload ||
              !payload.name ||
              !payload.description ||
              isLoading
            }>
            Import
          </Button>
        </WindowActionsBar>
      </Window>
    </>
  );
};
